import { useEffect, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { fetchPeplinkGraph } from "../../services/vesselService";
import { getCurrentMonthRange, getLast30DaysRange, getLastMonthRange } from "../../utils/helpers";

const CURRENT_MONTH = "month";
const LAST_MONTH = "lastMonth";
const LAST_30_DAYS = "last30Days";

const SplineAreaChartPeplink = ({ id, type, data, navigate, selected }) => {
	const [selectedRange, setSelectedRange] = useState(CURRENT_MONTH);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isDate, setIsDate] = useState({
		startDate: "",
		endDate: "",
	});
	const dispatch = useDispatch();
	const token = useSelector((state) => state.auth.user.token);
	const dropdownRef = useRef(null);

	useEffect(() => {
		if (data) {
			setIsLoading(false);
		}
	}, [data]);

	const timeRanges = {
		[LAST_30_DAYS]: "Last 30 Days",
		[CURRENT_MONTH]: "Current Month",
		24: "Last 24 Hours",
		168: "Last 7 Days",
	};

	const convertToGb = (mb) => {
		const value = Number(mb);
		if (isNaN(value)) return 0;
		return +(value / 1024).toFixed(2); // return number with 2 decimals
	};
	const now = new Date().getTime();

	const getTimeRange = (range) => {
		if (isDate?.startDate && isDate?.endDate) {
			const customStart = new Date(isDate.startDate);
			customStart.setHours(0, 0, 0, 0);
			const customEnd = new Date(isDate.endDate);
			customEnd.setHours(23, 59, 59, 999);
			return { start: customStart.getTime(), end: customEnd.getTime() };
		}
		if (range === CURRENT_MONTH) {
			const { start: monthStart, axisEnd } = getCurrentMonthRange();
			return { start: monthStart.getTime(), end: axisEnd.getTime() };
		}
		if (range === LAST_MONTH) {
			const { start: lastMonthStart, axisEnd } = getLastMonthRange();
			return { start: lastMonthStart.getTime(), end: axisEnd.getTime() };
		}
		if (range === LAST_30_DAYS) {
			const { start: last30Start, axisEnd } = getLast30DaysRange();
			return { start: last30Start.getTime(), end: axisEnd.getTime() };
		}
		const hours = Number(range);
		const end = now;
		const start = now - hours * 3600 * 1000;
		return { start, end };
	};

	const { start, end } = getTimeRange(selectedRange);
	const spanInDays = Math.round((end - start) / (24 * 3600 * 1000));
	const isDailyAxis = spanInDays >= 1;
	const series = [
		{
			name: "upload",
			data: data.map((entry) => ({
				x: entry.ts,
				y: convertToGb(entry.up),
			})),
		},
		{
			name: "download",
			data: data.map((entry) => ({
				x: entry.ts,
				y: convertToGb(entry.down),
			})),
		},
	];

	const ticks = [];

	const current = new Date(start);
	current.setHours(0, 0, 0, 0);

	const last = new Date(end);
	last.setHours(0, 0, 0, 0);

	while (current <= last) {
		ticks.push(current.getTime());
		current.setDate(current.getDate() + 1);
	}
	const options = {
		chart: {
			type: "area",
			height: 350,
			zoom: { enabled: false },
			toolbar: {
				show: false, // Hide the toolbar
			},
		},
		dataLabels: { enabled: false },
		stroke: { curve: "smooth" },
		xaxis: {
			type: "datetime",
			min: ticks[0],
			max: ticks[ticks.length - 1],
			tickAmount: ticks.length - 2,

			labels: {
				rotate: isDailyAxis ? -45 : 0,
				rotateAlways: isDailyAxis,
				hideOverlappingLabels: true,
				formatter: (value) =>
					isDailyAxis
						? new Date(value).toLocaleDateString("en-GB", {
								day: "2-digit",
								month: "short",
							})
						: new Date(value).toLocaleString("en-GB", {
								year: "numeric",
								month: "2-digit",
								day: "2-digit",
								hour: "2-digit",
								minute: "2-digit",
								hour12: false,
							}),
				style: {
					colors: "#FFFFFF",
					fontSize: "12px",
					fontWeight: "bold",
				},
			},
			axisBorder: { show: true, color: "#FFFFFF", height: 2 },
			axisTicks: { show: true, color: "#FFFFFF", height: 6 },
		},
		yaxis: {
			title: {
				style: {
					color: "#FFFFFF", // White Y-axis title
					fontSize: "12px",
					fontWeight: "bold", // Bold Y-axis title
				},
			},
			labels: {
				formatter: (value) => Number(value).toFixed(2) + " GB",
				style: {
					colors: "#FFFFFF", // White label color for Y-axis
					fontSize: "12px",
					fontWeight: "bold", // Bold Y-axis labels
				},
			},
			axisBorder: {
				show: true,
				color: "#FFFFFF", // White Y-axis border
				width: 2,
			},
			axisTicks: {
				show: true,
				color: "#FFFFFF", // White Y-axis ticks
				width: 6,
			},
		},
		tooltip: { x: { format: isDailyAxis ? "dd MMM yyyy " : "dd MMM yyyy HH:mm" } },
		legend: {
			position: "top",
			horizontalAlign: "right",
			offsetX: 0,
			offsetY: 0,
		},
		colors: ["#3A82F7", "#FF4560"],
		noData: {
			text: "No data available for this period",
			align: "center",
			verticalAlign: "middle",
			style: {
				color: "#FFFFFF",
				fontSize: "14px",
				fontWeight: "bold",
			},
		},
	};

	const toggleDropdown = () => {
		setIsDropdownOpen(!isDropdownOpen);
	};

	const handleSelectRange = async (range) => {
		setSelectedRange(range);
		setIsDropdownOpen(false);
		setIsLoading(true);
		setIsDate({ startDate: "", endDate: "" });

		try {
			if (range === CURRENT_MONTH) {
				console.log("++++");

				const { start: monthStart, end: monthEnd } = getCurrentMonthRange();
				await fetchPeplinkGraph(
					token,
					id,
					null,
					monthStart.toISOString(),
					monthEnd.toISOString(),
					selected,
					type,
					dispatch,
					navigate,
				);
			} else if (range === LAST_MONTH) {
				const { start: lastMonthStart, end: lastMonthEnd } = getLastMonthRange();
				await fetchPeplinkGraph(
					token,
					id,
					null,
					lastMonthStart.toISOString(),
					lastMonthEnd.toISOString(),
					selected,
					type,
					dispatch,
					navigate,
				);
			} else if (range === LAST_30_DAYS) {
				const { start: last30Start, end: last30End } = getLast30DaysRange();
				await fetchPeplinkGraph(
					token,
					id,
					null,
					last30Start.toISOString(),
					last30End.toISOString(),
					selected,
					type,
					dispatch,
					navigate,
				);
			} else {
				await fetchPeplinkGraph(token, id, range, null, null, selected, type, dispatch, navigate);
			}
			setIsLoading(false);
		} catch (error) {
			console.error("Error fetching data:", error);
			setIsLoading(false);
		}
	};

	const handleValueChange = async (newDate) => {
		setIsDate(newDate);
		setIsLoading(true);

		try {
			await fetchPeplinkGraph(
				token,
				id,
				null,
				newDate.startDate,
				newDate.endDate,
				selected,
				type,
				dispatch,
				navigate,
			);
			setIsLoading(false);
		} catch (error) {
			console.error("Error fetching data:", error);
			setIsLoading(false);
		}
	};

	// Function to close the dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div id={`${id}-${type}-chart`} className="w-full lg:w-auto">
			<div className="flex w-full flex-col items-center gap-4 md:flex-row">
				<div className="relative" ref={dropdownRef}>
					<button
						id="dropdownDefaultButton"
						onClick={() => setIsDropdownOpen(!isDropdownOpen)}
						className="inline-flex w-full items-center rounded-lg bg-blue-gray px-5 py-2.5 text-center font-medium text-sm text-white hover:bg-dark-blue focus:outline-none focus:ring-4 focus:ring-dark-blue md:w-auto"
						type="button"
					>
						{timeRanges[selectedRange]}
						<svg
							className="ml-3 h-2.5 w-2.5"
							aria-hidden="true"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 10 6"
						>
							<path
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="m1 1 4 4 4-4"
							/>
						</svg>
					</button>

					{isDropdownOpen && (
						<div
							id="dropdown"
							className="absolute z-10 mt-2 w-full divide-y divide-gray-100 rounded-lg bg-dark-blue shadow md:w-36"
						>
							<ul className="py-2 text-gray-200 text-sm">
								{Object.keys(timeRanges).map((range) => (
									<li key={range}>
										<a
											href="#"
											className="block px-2 py-2 hover:bg-blue-gray hover:text-white"
											onClick={() => {
												handleSelectRange(range);
												setIsDropdownOpen(false); // Fermer après sélection
											}}
										>
											{timeRanges[range]}
										</a>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
			</div>

			{isLoading ? (
				<div className="mt-4 text-center">
					<p>Loading...</p>
				</div>
			) : (
				<ReactApexChart options={options} series={series} type="area" height={350} />
			)}
		</div>
	);
};

export default SplineAreaChartPeplink;
